import { useCallback, useEffect, useState } from 'react';
import { fetchAuthors } from '../Authors/authorsApi';
import { settingsListApiError, type AuthorRow } from '../Settings/lists/singleTableListSupport';
import { deleteBook, fetchBooks, type BookListItem } from './bookApi';

export interface LoadBooksOptions {
  silent?: boolean;
}

export type BookWithAuthor = BookListItem & {
  author: AuthorRow | null;
};

function mergeBooksWithAuthors(books: BookListItem[], authors: AuthorRow[]): BookWithAuthor[] {
  const byId = new Map<string, AuthorRow>();
  for (const a of authors) {
    byId.set(String(a.id), a);
  }
  return books.map((book) => ({
    ...book,
    author: byId.get(String(book.author_id)) ?? null,
  }));
}

export function useBooksList() {
  const [rows, setRows] = useState<BookWithAuthor[]>([]);
  const [authors, setAuthors] = useState<AuthorRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [deletingId, setDeletingId] = useState<string | null>(null);

  const load = useCallback(async (options?: LoadBooksOptions) => {
    const silent = options?.silent === true;
    if (!silent) {
      setLoading(true);
      setError(null);
    }
    try {
      const [items, authorRows] = await Promise.all([fetchBooks(), fetchAuthors()]);
      setAuthors(authorRows);
      setRows(mergeBooksWithAuthors(items, authorRows));
    } catch (e: unknown) {
      if (!silent) {
        setError(settingsListApiError(e, 'Номууд эсвэл зохиолчдыг ачааллахад алдаа гарлаа.'));
        setRows([]);
        setAuthors([]);
      }
    } finally {
      if (!silent) setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const deleteById = useCallback(
    async (id: string) => {
      setDeletingId(id);
      setError(null);
      try {
        await deleteBook(id);
        await load({ silent: true });
      } catch (e: unknown) {
        setError(settingsListApiError(e, 'Ном устгахад алдаа гарлаа.'));
      } finally {
        setDeletingId(null);
      }
    },
    [load],
  );

  return { rows, loading, error, load, authors, deleteById, deletingId };
}
